/**
 * Go2Rtc WebRTC player (adapted from go2rtc video-rtc.js, MIT license).
 * Simplified for React integration — WebRTC primary, MJPEG fallback.
 */
export class Go2RtcPlayer {
  constructor(videoEl, options = {}) {
    this.video = videoEl;
    this.mode = options.mode || 'webrtc,mjpeg';
    this.media = options.media || 'video,audio';
    this.pcConfig = options.pcConfig || {
      bundlePolicy: 'max-bundle',
      iceServers: [
        { urls: ['stun:stun.cloudflare.com:3478', 'stun:stun.l.google.com:19302'] },
      ],
      sdpSemantics: 'unified-plan',
    };
    this.onStateChange = options.onStateChange || (() => {});
    this.playUnmuted = options.playUnmuted === true;
    this.keepSignaling = options.keepSignaling === true;
    this.micTrack = options.micTrack || null;
    this.micStream = options.micStream || null;
    this.wsURL = '';
    this.ws = null;
    this.pc = null;
    this.ondata = null;
    this.onmessage = null;
    this.connectTS = 0;
    this.reconnectTID = 0;
    this.DISCONNECT_TIMEOUT = 5000;
    this.RECONNECT_TIMEOUT = 15000;
    this.destroyed = false;
    // True once WebRTC reaches "connected". Lets us close the signaling WS
    // (media flows P2P afterwards) without onClose tearing the PC back down.
    this.webrtcConnected = false;
  }

  setState(state, detail = '') {
    this.onStateChange(state, detail);
  }

  connect(wsURL) {
    if (this.destroyed) return;
    this.wsURL = wsURL;
    this.openConnection();
  }

  disconnect() {
    this.webrtcConnected = false;
    if (this.reconnectTID) {
      clearTimeout(this.reconnectTID);
      this.reconnectTID = 0;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    if (this.pc) {
      this.pc.getSenders().forEach((sender) => {
        if (sender.track) sender.track.stop();
      });
      this.pc.close();
      this.pc = null;
    }
    if (this.video) {
      this.video.srcObject = null;
      this.video.removeAttribute('poster');
    }
  }

  destroy() {
    this.destroyed = true;
    this.disconnect();
    if (this.micStream) {
      this.micStream.getTracks().forEach((t) => t.stop());
      this.micStream = null;
      this.micTrack = null;
    }
  }

  setTransmitting(enabled) {
    if (this.micTrack) {
      this.micTrack.enabled = !!enabled;
    }
  }

  /** Retry audible playback from a user gesture after the browser blocked it. */
  resume() {
    if (!this.video) return Promise.resolve();
    this.video.muted = false;
    return this.video.play().then(() => this.setState('live'));
  }

  send(value) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(value));
    }
  }

  openConnection() {
    if (this.destroyed || !this.wsURL || this.ws) return;

    this.setState('connecting');
    this.connectTS = Date.now();
    this.ws = new WebSocket(this.wsURL);
    this.ws.binaryType = 'arraybuffer';
    this.ws.addEventListener('open', () => this.onOpen());
    this.ws.addEventListener('close', () => this.onClose());
    this.ws.addEventListener('error', () => this.setState('error', 'WebSocket error'));
  }

  onOpen() {
    this.ws.addEventListener('message', (ev) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data);
        if (this.onmessage) {
          Object.values(this.onmessage).forEach((handler) => handler(msg));
        }
      } else if (this.ondata) {
        this.ondata(ev.data);
      }
    });

    this.ondata = null;
    this.onmessage = {};

    if (this.mode.includes('webrtc') && 'RTCPeerConnection' in window) {
      this.setupWebRTC();
    } else if (this.mode.includes('mjpeg')) {
      this.setupMjpeg();
    }
  }

  onClose() {
    if (this.destroyed) return;
    this.ws = null;
    // WS was closed intentionally after WebRTC connected — the media keeps
    // flowing P2P, so don't tear down the live PC or schedule a reconnect.
    if (this.webrtcConnected && this.pc) return;
    if (this.pc) {
      this.pc.close();
      this.pc = null;
    }
    const delay = Math.max(this.RECONNECT_TIMEOUT - (Date.now() - this.connectTS), 0);
    this.reconnectTID = setTimeout(() => {
      this.reconnectTID = 0;
      this.openConnection();
    }, delay);
    this.setState('reconnecting');
  }

  setupWebRTC() {
    const pc = new RTCPeerConnection(this.pcConfig);

    pc.addEventListener('icecandidate', (ev) => {
      const candidate = ev.candidate ? ev.candidate.toJSON().candidate : '';
      this.send({ type: 'webrtc/candidate', value: candidate });
    });

    // Входящее собирается по мере прибытия. Отбирать дорожки по `recvonly`
    // нельзя: микрофон Оператора садится на тот же аудио-трансивер и переводит
    // его в `sendrecv`, после чего голос объекта молча терялся при сборке потока.
    const remote = new MediaStream();
    pc.addEventListener('track', (ev) => {
      remote.addTrack(ev.track);
    });

    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'connected') {
        this.video.srcObject = remote;
        this.video.muted = !this.playUnmuted;
        this.video
          .play()
          .then(() => this.setState('live'))
          .catch(() => {
            // Autoplay policy. Falling back to muted is fine for a viewer, but
            // for a Разговор silence is the failure itself — the caller has to
            // ask for the click that unblocks it.
            if (this.playUnmuted) {
              this.setState('blocked');
              return;
            }
            this.video.muted = true;
            this.video.play().catch(() => {});
            this.setState('live');
          });

        // Mark connected BEFORE closing the WS so onClose leaves the PC alone.
        this.webrtcConnected = true;
        // Talk keeps signaling open: operator presence is heartbeated on this
        // socket, and ICE/restart still needs it. Video viewers drop it.
        if (this.ws && !this.keepSignaling) {
          this.ws.close();
          this.ws = null;
        }
      } else if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        const droppedAfterConnect = this.webrtcConnected;
        this.webrtcConnected = false;
        pc.close();
        this.pc = null;
        if (droppedAfterConnect) {
          // Media died after a working connection. Talk may still hold the
          // signaling WS; drop it so openConnection can handshake again.
          this.setState('reconnecting');
          if (this.ws) {
            this.ws.close();
            this.ws = null;
          }
          this.openConnection();
        } else if (this.mode.includes('mjpeg')) {
          this.setupMjpeg();
        } else {
          this.setState('error', 'WebRTC connection failed');
        }
      }
    });

    this.onmessage.webrtc = (msg) => {
      switch (msg.type) {
        case 'webrtc/candidate':
          pc.addIceCandidate({ candidate: msg.value, sdpMid: '0' }).catch(() => {});
          break;
        case 'webrtc/answer':
          pc.setRemoteDescription({ type: 'answer', sdp: msg.value }).catch(() => {});
          break;
        case 'error':
          if (msg.value?.includes('webrtc/offer') && this.mode.includes('mjpeg')) {
            pc.close();
            this.pc = null;
            this.setupMjpeg();
          } else {
            // Without a fallback the caller would sit on "connecting" forever,
            // so a source that go2rtc cannot open has to be reported.
            pc.close();
            this.pc = null;
            this.setState('error', msg.value || 'go2rtc error');
          }
          break;
        default:
          break;
      }
    };

    this.createOffer(pc).then((offer) => {
      this.send({ type: 'webrtc/offer', value: offer.sdp });
    });

    this.pc = pc;
  }

  async createOffer(pc) {
    for (const kind of ['video', 'audio']) {
      if (this.media.includes(kind)) {
        pc.addTransceiver(kind, { direction: 'recvonly' });
      }
    }
    if (this.micTrack && this.micStream) {
      this.micTrack.enabled = false;
      pc.addTrack(this.micTrack, this.micStream);
    }
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    return offer;
  }

  setupMjpeg() {
    this.ondata = (data) => {
      this.video.controls = false;
      this.video.srcObject = null;
      this.video.poster = `data:image/jpeg;base64,${Go2RtcPlayer.bufferToBase64(data)}`;
      this.setState('snapshot');
    };
    this.send({ type: 'mjpeg' });
  }

  static bufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }
}

export function buildStreamWsUrl(wsPath) {
  const token = localStorage.getItem('authToken') || '';
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  let path = wsPath.startsWith('/') ? wsPath : `/${wsPath}`;
  // Avoid /api/api/... when backend path still includes /api prefix
  if (apiBase.endsWith('/api') && path.startsWith('/api/')) {
    path = path.slice(4);
  }

  let wsOrigin;
  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    wsOrigin = apiBase.replace(/^http/i, (m) => (m.toLowerCase() === 'https' ? 'wss' : 'ws'));
  } else {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    wsOrigin = `${protocol}//${window.location.host}${apiBase}`;
  }

  const base = wsOrigin.replace(/\/$/, '');
  const sep = path.includes('?') ? '&' : '?';
  return `${base}${path}${sep}token=${encodeURIComponent(token)}`;
}

export function buildTalkWsUrl(wsPath) {
  return buildStreamWsUrl(wsPath);
}
